// frontend/src/components/deals/dialogs/AppointmentDialog.tsx
import { useEffect, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import AppDialog from "@/components/common/AppDialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { FormCalendar, FormInput, FormSelect, FormTextarea } from "@/components/forms/FormControls";
import { appointmentSchema, type AppointmentValues } from "@/schemas/deals";
import type { Appointment, AppointmentPayload, DealDetail } from "@/types/deals.types";
import { addMinutes, appointmentPayload, browserTimeZone, isoToLocalParts } from "@/utils/appointments.utils";
import { APPOINTMENT_KIND_LABELS } from "@/constant/deals.constant";
import { useGetAppointmentAttendees } from "@/hooks/useDeals";

interface Props {
  open: boolean;
  onClose: () => void;
  deal: DealDetail;
  appointment?: Appointment | null;
  busy?: boolean;
  onSubmit: (payload: AppointmentPayload) => void;
}

export default function AppointmentDialog({ open, onClose, deal, appointment, busy, onSubmit }: Props) {
  const isEdit = Boolean(appointment);
  const fieldArea = useRef<HTMLDivElement>(null);
  const { data: roster = [], isLoading: loadingPeople, isError: peopleError, refetch } = useGetAppointmentAttendees(deal.id, open);
  const people = roster.filter((p) => p.user_id).map((p) => ({ user_id: p.user_id as string, name: `${p.name} · ${p.role.replaceAll("_", " ")}` }));
  const defaults = useMemo<AppointmentValues>(() => {
    if (appointment) {
      const s = isoToLocalParts(appointment.starts_at, appointment.timezone);
      const e = isoToLocalParts(appointment.ends_at, appointment.timezone);
      return {
        kind: appointment.kind, title: appointment.title, date: s.date, start_time: s.time, end_time: e.time,
        timezone: appointment.timezone, location: appointment.location, notes: appointment.notes,
        attendee_user_ids: appointment.attendees.filter((a) => a.user_id).map((a) => a.user_id as string),
        guest_emails: appointment.attendees.filter((a) => a.role === "guest").map((a) => a.email).join(", "),
      };
    }
    return { kind: "showing", title: `Showing — ${deal.address}`, date: "", start_time: "10:00", end_time: "11:00",
      timezone: browserTimeZone(), location: deal.address || "", notes: "", attendee_user_ids: [], guest_emails: "" };
  }, [appointment, deal.address]);
  const form = useForm<AppointmentValues>({ resolver: zodResolver(appointmentSchema), defaultValues: defaults });
  useEffect(() => { if (open) form.reset(defaults); }, [open, defaults, form]);
  const kind = form.watch("kind");
  useEffect(() => {
    if (!open || isEdit) return;
    const title = form.getValues("title");
    const automaticTitles = Object.values(APPOINTMENT_KIND_LABELS).map((label) => `${label} — ${deal.address}`);
    if (automaticTitles.includes(title)) {
      form.setValue("title", `${APPOINTMENT_KIND_LABELS[kind]} — ${deal.address}`);
    }
  }, [kind, deal.address, form, isEdit, open]);
  const start = form.watch("start_time");
  useEffect(() => {
    if (!isEdit && start) form.setValue("end_time", addMinutes(start, 60));
  }, [start, isEdit, form]);

  const submit = (v: AppointmentValues) => onSubmit(appointmentPayload(v, appointment));
  const revealError = () => {
    // Wait for the error text to render, then reveal the entire field and message.
    requestAnimationFrame(() => {
      fieldArea.current?.querySelector('[aria-invalid="true"]')?.parentElement?.scrollIntoView({ block: "nearest" });
    });
  };

  return (
    <AppDialog open={open} onOpenChange={(next) => { if (!next && !busy) onClose(); }} title={isEdit ? "Edit appointment" : "Schedule a showing or meeting"} eyebrow={isEdit ? "Edit" : "New"} contentClassName="flex w-[calc(100vw-2rem)] flex-col gap-0 overflow-hidden p-0 sm:max-w-xl" headerClassName="shrink-0 border-b border-dash-border px-5 py-4 pr-14 text-left sm:px-6 sm:pr-16" testId="appointment-dialog">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(submit, revealError)} className="flex min-h-0 flex-1 flex-col" noValidate>
          <div ref={fieldArea} className="min-h-0 flex-1 overflow-y-auto scroll-py-4 px-5 py-5 sm:px-6" data-testid="appointment-dialog-fields">
            <fieldset disabled={busy} className="flex min-w-0 flex-col gap-5 border-0 p-0">
              {!isEdit && (
                <FormSelect name="kind" label="Type" options={Object.entries(APPOINTMENT_KIND_LABELS).map(([value, label]) => ({ value, label }))} data-testid="appointment-kind" required />
              )}
              <FormInput name="title" label="Title" aria-label="Title" data-testid="appointment-title" required />
              <div className="flex flex-col gap-4">
                <FormCalendar name="date" label="Date" data-testid="appointment-date" required />
                <div className="grid grid-cols-2 gap-4">
                  <FormInput name="start_time" label="Starts" aria-label="Starts" type="time" data-testid="appointment-start" required />
                  <FormInput name="end_time" label="Ends" aria-label="Ends" type="time" data-testid="appointment-end" required />
                </div>
              </div>
              <p className="-mt-2 text-xs text-dash-muted">Times are in {defaults.timezone.replaceAll("_", " ")}.</p>
              <FormInput name="location" label="Location" aria-label="Location" data-testid="appointment-location" />
              {/* FormCheckbox binds one boolean, so the multi-select is a plain
                  FormField over the id array. */}
              <FormField control={form.control} name="attendee_user_ids" render={({ field }) => (
                <FormItem className="flex flex-col gap-2">
                  <FormLabel>Who's attending</FormLabel>
                  {loadingPeople && <p className="text-xs text-dash-muted" role="status">Loading people…</p>}
                  {peopleError && <p className="text-sm text-dash-rust" role="alert">Couldn’t load people. <button type="button" className="underline" onClick={() => refetch()}>Try again</button></p>}
                  {people.map((p) => (
                    <label key={p.user_id} className="flex min-w-0 items-start gap-3 text-sm leading-5 text-dash-ink">
                      <Checkbox
                        className="mt-0.5 shrink-0"
                        checked={field.value.includes(p.user_id)}
                        onCheckedChange={(on) => field.onChange(on ? [...field.value, p.user_id] : field.value.filter((id: string) => id !== p.user_id))}
                        data-testid={`appointment-attendee-${p.user_id}`}
                      />
                      <span className="min-w-0 break-words">{p.name}</span>
                    </label>
                  ))}
                </FormItem>
              )} />
              <FormInput name="guest_emails" label="Guests outside Stoop (optional)" aria-label="Guests outside Stoop" placeholder="buyer.agent@brokerage.com" description="Up to 5 email addresses, separated by commas." data-testid="appointment-guests" />
              <FormTextarea name="notes" label="Notes (optional)" rows={3} placeholder="Lockbox code, parking, what to bring…" data-testid="appointment-notes" />
              <p className="text-xs text-dash-muted">Everyone invited gets an email with a calendar invite. Changes and cancellations are sent automatically.</p>
            </fieldset>
          </div>
          <div className="flex shrink-0 flex-wrap justify-end gap-3 border-t border-dash-border bg-white px-5 py-4 sm:px-6" data-testid="appointment-dialog-actions">
            <Button type="button" variant="dashboardOutline" className="px-3 sm:px-4" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button type="submit" variant="dashboardPrimary" className="px-3 sm:px-4" loading={busy} disabled={loadingPeople || peopleError} data-testid="appointment-submit">{busy ? (isEdit ? "Saving…" : "Scheduling…") : (isEdit ? "Save and notify" : "Schedule and invite")}</Button>
          </div>
        </form>
      </Form>
    </AppDialog>
  );
}
